/**
 * Raporlama kılavuzu tarayıcısı.
 *
 * Çalışma türünü (RCT / gözlemsel / sistematik derleme) metinden kestirir ve
 * yalnızca EN KRİTİK maddeleri kontrol eder: CONSORT 10, STROBE 8, PRISMA 8.
 * Eksik maddeler "warning" olarak raporlanır ama decisive=false'dur: kılavuz
 * maddeleri sıklıkla ek materyalde verilir, bu yüzden nihai notu düşürmezler.
 */

import type {
	GuidelineItem,
	GuidelineKey,
	GuidelineResult,
	GuidelineStudyType,
	Issue,
	IssueCode
} from '../../types';
import type { DocumentIndex } from '../../text/document';
import { excerpt } from '../../text/normalize';
import { classifyStudy } from '../../design/classify';

interface ItemSpec {
	key: string;
	labelEn: string;
	labelTr: string;
	re: RegExp;
}

const CONSORT: ItemSpec[] = [
	{
		key: 'randomization',
		labelEn: 'Randomisation / allocation method',
		labelTr: 'Randomizasyon / atama yöntemi',
		re: /randomi[sz]ation|randomi[sz]ed|random sequence|allocation sequence|rastgele|randomizasyon/iu
	},
	{
		key: 'blinding',
		labelEn: 'Blinding (masking)',
		labelTr: 'Körleme',
		re: /blind(?:ing|ed)|double[- ]blind|single[- ]blind|masking|körleme|kör\b/iu
	},
	{
		key: 'flow_diagram',
		labelEn: 'CONSORT flow diagram',
		labelTr: 'CONSORT akış diyagramı',
		re: /consort[^.\n]{0,30}diagram|flow diagram|participant flow diagram|akış diyagramı/iu
	},
	{
		key: 'registration',
		labelEn: 'Trial registration number',
		labelTr: 'Deneme kayıt numarası',
		re: /trial registration|registration number|NCT\d{4,}|ISRCTN\d+|clinicaltrials\.gov|kayıt numarası|sicil/iu
	},
	{
		key: 'primary_outcome',
		labelEn: 'Primary outcome definition',
		labelTr: 'Birincil sonuç tanımı',
		re: /primary outcome|primary endpoint|birincil sonuç|birincil sonlanım/iu
	},
	{
		key: 'sample_size',
		labelEn: 'Sample size / power calculation',
		labelTr: 'Örneklem büyüklüğü / güç hesabı',
		re: /sample size (?:calculation|estimation)|power analysis|güç analizi|örneklem (?:büyüklüğü|hacmi)/iu
	},
	{
		key: 'itt',
		labelEn: 'Intention-to-treat analysis',
		labelTr: 'Intention-to-treat (ITT) analizi',
		re: /intention[- ]to[- ]treat|\bITT\b|tedavi niyeti/iu
	},
	{
		key: 'participant_flow',
		labelEn: 'Participant flow (enrolment, allocation, follow-up)',
		labelTr: 'Katılımcı akışı (kayıt, atama, izlem)',
		re: /participant flow|enrol(?:l)?ment|allocation|follow[- ]up|katılımcı akışı|kayıt|atama|izlem/iu
	},
	{
		key: 'adverse_events',
		labelEn: 'Adverse events / harms',
		labelTr: 'Yan etkiler / zararlar',
		re: /adverse events?|side effects?|harms\b|yan etki|istenmeyen olay/iu
	},
	{
		key: 'generalizability',
		labelEn: 'Generalizability / external validity',
		labelTr: 'Genellenebilirlik / dış geçerlilik',
		re: /generaliz(?:ability|able)|external validity|genellenebilirlik|dış geçerlilik/iu
	}
];

const STROBE: ItemSpec[] = [
	{
		key: 'design',
		labelEn: 'Study design',
		labelTr: 'Çalışma deseni',
		re: /cohort|cross[- ]sectional|case[- ]control|study design|kohort|kesitsel|vaka[- ]kontrol|çalışma deseni|desen/iu
	},
	{
		key: 'setting',
		labelEn: 'Setting and study dates',
		labelTr: 'Ortam ve çalışma tarihleri',
		re: /setting|study period|study dates|between \d{4}|20\d{2}\s*(?:-|–|and|ve)\s*20\d{2}|ortam|çalışma dönemi/iu
	},
	{
		key: 'eligibility',
		labelEn: 'Eligibility criteria',
		labelTr: 'Katılımcı seçim ölçütleri',
		re: /eligibility|inclusion criteria|exclusion criteria|dahil edilme|dışlama|uygunluk/iu
	},
	{
		key: 'variables',
		labelEn: 'Outcome, exposure and covariates',
		labelTr: 'Sonuç, maruziyet ve kovaryantlar',
		re: /outcome variables?|exposure|covariates?|değişken|maruziyet|kovaryant/iu
	},
	{
		key: 'sample_size',
		labelEn: 'Sample size rationale',
		labelTr: 'Örneklem büyüklüğü gerekçesi',
		re: /sample size|power analysis|örneklem (?:büyüklüğü|hacmi)|güç analizi/iu
	},
	{
		key: 'statistics',
		labelEn: 'Statistical methods (confounders, subgroups)',
		labelTr: 'İstatistiksel yöntemler (karıştırıcılar, alt gruplar)',
		re: /confound|subgroup|regression|multivariable|istatistik|karıştırıcı|alt grup/iu
	},
	{
		key: 'participant_flow',
		labelEn: 'Participant numbers / flow',
		labelTr: 'Katılımcı sayıları / akışı',
		re: /participants|numbers|flow|katılımcı|sayı|akış/iu
	},
	{
		key: 'limitations',
		labelEn: 'Limitations and bias',
		labelTr: 'Sınırlılıklar ve yanlılık',
		re: /limitation|bias|sınırlılık|yanlılık/iu
	}
];

const PRISMA: ItemSpec[] = [
	{
		key: 'registration',
		labelEn: 'Protocol registration (PROSPERO)',
		labelTr: 'Protokol kaydı (PROSPERO)',
		re: /PROSPERO|protocol registration|registered protocol|registration number|NCT\d|ISRCTN|kayıt numarası|protokol kaydı/iu
	},
	{
		key: 'search',
		labelEn: 'Search strategy and databases',
		labelTr: 'Arama stratejisi ve veri tabanları',
		re: /search strategy|databases? searched|PubMed|Web of Science|Scopus|arama stratejisi|veri taban/iu
	},
	{
		key: 'eligibility',
		labelEn: 'Eligibility criteria / PICOS',
		labelTr: 'Seçim ölçütleri / PICOS',
		re: /PICOS|eligibility criteria|inclusion criteria|seçim ölçütleri|dahil edilme/iu
	},
	{
		key: 'extraction',
		labelEn: 'Data extraction',
		labelTr: 'Veri çıkarımı',
		re: /data extraction|data collection|veri çıkarımı|veri toplama/iu
	},
	{
		key: 'risk_of_bias',
		labelEn: 'Risk-of-bias assessment',
		labelTr: 'Yanlılık riski değerlendirmesi',
		re: /risk of bias|quality assessment|RoB\b|yanlılık riski|kalite değerlendirme/iu
	},
	{
		key: 'synthesis',
		labelEn: 'Synthesis method',
		labelTr: 'Sentez yöntemi',
		re: /meta[- ]?analys|random[- ]effects|synthesis|sentez|rastgele etkiler/iu
	},
	{
		key: 'flow_diagram',
		labelEn: 'PRISMA flow diagram',
		labelTr: 'PRISMA akış diyagramı',
		re: /prisma[^.\n]{0,30}diagram|flow diagram|akış diyagramı/iu
	},
	{
		key: 'publication_bias',
		labelEn: 'Publication bias / funnel plot',
		labelTr: 'Yayın yanlılığı / huni grafiği',
		re: /publication bias|funnel plot|yayın yanlılığı|huni grafi/iu
	}
];

const SPECS: Record<GuidelineKey, ItemSpec[]> = {
	consort: CONSORT,
	strobe: STROBE,
	prisma: PRISMA
};

export interface GuidelinesEngineOutput {
	result: GuidelineResult;
	issues: Issue[];
}

export function detectStudyType(text: string): GuidelineStudyType {
	const classification = classifyStudy(text);
	if (classification.scope === 'lab' || classification.scope === 'animal') return 'unknown';
	if (classification.design === 'rct') return 'rct';
	if (classification.design === 'observational') return 'observational';
	if (classification.design === 'systematic-review') return 'systematic-review';
	return 'unknown';
}

export function runGuidelinesEngine(index: DocumentIndex): GuidelinesEngineOutput {
	const text = index.text;
	const studyType = detectStudyType(text);
	const issues: Issue[] = [];
	const push = (issue: Omit<Issue, 'id' | 'decisive'> & { decisive?: boolean }) => {
		issues.push({
			...issue,
			id: `guideline-${issues.length + 1}`,
			decisive: issue.decisive ?? false
		});
	};

	const guideline: GuidelineKey | null =
		studyType === 'rct'
			? 'consort'
			: studyType === 'observational'
				? 'strobe'
				: studyType === 'systematic-review'
					? 'prisma'
					: null;

	if (!guideline) {
		const result: GuidelineResult = {
			guideline: null,
			studyType,
			items: [],
			present: 0,
			total: 0,
			missing: []
		};
		push({
			code: 'no_guideline_match',
			category: 'structure',
			severity: 'info',
			title: 'Raporlama kılavuzu eşleşmedi',
			detail:
				'Çalışma türü CONSORT, STROBE veya PRISMA ile eşleştirilemedi; genel yapı kontrolü uygulandı.',
			suggestion: 'Çalışma türünüzü Yöntem bölümünde açıkça belirtin (RCT, kohort, kesitsel, sistematik derleme).',
			location: {},
			confidence: 'medium',
			decisive: false,
			meta: { studyType }
		});
		return { result, issues };
	}

	const specs = SPECS[guideline];
	const items: GuidelineItem[] = specs.map((spec) => {
		spec.re.lastIndex = 0;
		const m = spec.re.exec(text);
		return {
			key: spec.key,
			labelEn: spec.labelEn,
			labelTr: spec.labelTr,
			present: Boolean(m),
			evidence: m ? excerpt(text.slice(m.index, m.index + 220), 160) : undefined
		};
	});
	const missing = items.filter((i) => !i.present).map((i) => i.key);

	const guidelineName = guideline === 'consort' ? 'CONSORT' : guideline === 'strobe' ? 'STROBE' : 'PRISMA';
	push({
		code: 'guideline_detected',
		category: 'structure',
		severity: 'info',
		title: `${guidelineName} kılavuzu uygulanabilir`,
		detail: `Çalışma türü ${studyType === 'rct' ? 'randomize kontrollü deneme' : studyType === 'observational' ? 'gözlemsel' : 'sistematik derleme'} olarak kestirildi; ${guidelineName} kontrol listesinin ${items.length - missing.length}/${items.length} maddesi metinde bulundu.`,
		suggestion:
			missing.length > 0
				? 'Eksik maddeleri Yöntem/Bulgular bölümüne veya ek materyale ekleyin.'
				: undefined,
		location: {},
		confidence: 'medium',
		decisive: false,
		meta: {
			guideline,
			studyType,
			present: items.length - missing.length,
			total: items.length
		}
	});

	for (const item of items) {
		if (item.present) continue;
		push({
			code: `${guideline}_missing_${item.key}` as IssueCode,
			category: 'structure',
			severity: 'warning',
			title: `${guidelineName} maddesi eksik: ${item.labelTr}`,
			detail: `Seçilen kılavuza göre "${item.labelTr}" maddesi metinde tespit edilemedi.`,
			suggestion: 'Bu maddeyi açıkça raporlayın veya ek materyalde belirtin.',
			location: {},
			confidence: 'medium',
			decisive: false,
			meta: { guideline, item: item.key, itemEn: item.labelEn, itemTr: item.labelTr }
		});
	}

	const result: GuidelineResult = {
		guideline,
		studyType,
		items,
		present: items.length - missing.length,
		total: items.length,
		missing
	};

	return { result, issues };
}
